import axios, { AxiosInstance } from 'axios';

export interface OidcDiscoveryDocument {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  jwks_uri?: string;
  registration_endpoint?: string;
  code_challenge_methods_supported?: string[];
}

interface TokenExchangeParams {
  code: string;
  redirectUri: string;
  codeVerifier: string;
  clientId: string;
  clientSecret: string;
}

interface RefreshTokenParams {
  refreshToken: string;
  clientId: string;
  clientSecret: string;
}

export class SyberKeyIdpSdk {
  private http: AxiosInstance;
  private baseUrl: string;
  private discoveryCache: OidcDiscoveryDocument | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.http = axios.create({ baseURL: this.baseUrl });
  }

  async health(): Promise<any> {
    const response = await this.http.get('/idp/v1/health');
    return response.data;
  }

  async getDiscovery(): Promise<OidcDiscoveryDocument> {
    if (this.discoveryCache) {
      return this.discoveryCache;
    }

    // Prefer new IdP integration discovery endpoint.
    try {
      const response = await this.http.get('/idp/v1/.well-known/openid-configuration');
      this.discoveryCache = response.data;
      return response.data;
    } catch (_error) {
      // Fallback to root discovery alias for backward compatibility.
      const fallbackResponse = await this.http.get('/.well-known/openid-configuration');
      this.discoveryCache = fallbackResponse.data;
      return fallbackResponse.data;
    }
  }

  async exchangeCodeForTokens(params: TokenExchangeParams): Promise<any> {
    const discovery = await this.getDiscovery();
    const requestBody = new URLSearchParams({
      grant_type: 'authorization_code',
      code: params.code,
      redirect_uri: params.redirectUri,
      code_verifier: params.codeVerifier,
      client_id: params.clientId
    });

    const response = await axios.post(discovery.token_endpoint, requestBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${btoa(`${params.clientId}:${params.clientSecret}`)}`
      }
    });

    return response.data;
  }

  async getUserInfo(accessToken: string): Promise<any> {
    const discovery = await this.getDiscovery();
    const response = await axios.get(discovery.userinfo_endpoint, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      }
    });
    return response.data;
  }

  async refreshAccessToken(params: RefreshTokenParams): Promise<any> {
    const discovery = await this.getDiscovery();
    const response = await axios.post(
      discovery.token_endpoint,
      new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: params.refreshToken,
        client_id: params.clientId
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${btoa(`${params.clientId}:${params.clientSecret}`)}`
        }
      }
    );
    return response.data;
  }

  async buildAuthorizationUrl(params: {
    clientId: string;
    redirectUri: string;
    scope: string;
    state: string;
    codeChallenge: string;
    clientName?: string;
    source?: string;
  }): Promise<string> {
    const discovery = await this.getDiscovery();
    const query = new URLSearchParams({
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      response_type: 'code',
      scope: params.scope,
      state: params.state,
      code_challenge: params.codeChallenge,
      code_challenge_method: 'S256'
    });

    if (params.clientName) {
      query.set('client_name', params.clientName);
    }
    if (params.source) {
      query.set('source', params.source);
    }

    return `${discovery.authorization_endpoint}?${query.toString()}`;
  }
}

